import { apiRequest } from '@/lib/api/client'
import type {
  Ga4ConversionsDashboardPayload,
  Ga4ConversionsEventDetailPayload,
  Ga4ConversionsEventsResponse,
  Ga4ConversionsEventStatus,
} from '@/types/google-conversions'

export interface Ga4ConversionsDateFilters {
  dateFrom?: string | null
  dateTo?: string | null
}

export interface Ga4ConversionsEventsFilters extends Ga4ConversionsDateFilters {
  page?: number
  perPage?: number
  status?: Ga4ConversionsEventStatus | ''
  q?: string | null
}

function buildQuery(params: Record<string, string | number | null | undefined>): string {
  const search = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    search.set(key, String(value))
  })

  const query = search.toString()
  return query ? `?${query}` : ''
}

export async function fetchGa4ConversionsDashboard(
  campaignId: number,
  filters: Ga4ConversionsDateFilters = {},
  token?: string | null
): Promise<Ga4ConversionsDashboardPayload> {
  const query = buildQuery({
    date_from: filters.dateFrom ?? undefined,
    date_to: filters.dateTo ?? undefined,
  })

  return apiRequest<Ga4ConversionsDashboardPayload>(
    `/api/campaigns/${campaignId}/ga4-conversions/dashboard${query}`,
    {
      method: 'GET',
      requireAuth: true,
      token: token ?? undefined,
    }
  )
}

export async function fetchGa4ConversionsEvents(
  campaignId: number,
  filters: Ga4ConversionsEventsFilters = {},
  token?: string | null
): Promise<Ga4ConversionsEventsResponse> {
  const query = buildQuery({
    page: filters.page ?? 1,
    per_page: filters.perPage ?? 20,
    date_from: filters.dateFrom ?? undefined,
    date_to: filters.dateTo ?? undefined,
    status: filters.status || undefined,
    q: filters.q?.trim() ? filters.q.trim() : undefined,
  })

  return apiRequest<Ga4ConversionsEventsResponse>(
    `/api/campaigns/${campaignId}/ga4-conversions/events${query}`,
    {
      method: 'GET',
      requireAuth: true,
      token: token ?? undefined,
    }
  )
}

export async function fetchGa4ConversionsEventDetail(
  campaignId: number,
  eventId: number,
  token?: string | null
): Promise<Ga4ConversionsEventDetailPayload> {
  return apiRequest<Ga4ConversionsEventDetailPayload>(
    `/api/campaigns/${campaignId}/ga4-conversions/events/${eventId}`,
    {
      method: 'GET',
      requireAuth: true,
      token: token ?? undefined,
    }
  )
}
